import { useEffect, useState } from "react";
import { api, subscribeSSE, type LiveBonus } from "@/api/client";

// Centralised live-bonus state.
// - One initial fetch on mount
// - Refetched whenever the global SSE feed announces a game:update
//   (the live-sync cron publishes there every minute during a match)
// - Also re-polled as a safety net in case the SSE drops
//
// Herd control: the server caches this endpoint for 10s, and we add
// client-side jitter on top. Without it, every connected client would
// refetch in the same instant the SSE event lands — thousands of
// simultaneous requests timed to the exact same tick. The random 0-8s
// delay spreads that burst across the cache window; the polling interval
// gets a per-client random offset for the same reason.
const EMPTY: LiveBonus = { hasLive: false, liveGames: [], perUser: {}, perGroup: {}, generatedAt: "" };

export function useLiveBonus() {
  const [data, setData] = useState<LiveBonus>(EMPTY);

  useEffect(() => {
    let cancelled = false;
    let pendingTimer: ReturnType<typeof setTimeout> | null = null;

    const fetchOnce = async () => {
      try {
        const d = await api.rankings.liveBonus();
        if (!cancelled) setData(d);
      } catch {
        // silent — the UI just won't show live points
      }
    };

    // Jittered refetch: collapse multiple triggers into one pending fetch.
    // Several SSE events can land in the same second (one per live game);
    // only the first schedules a fetch, the rest are no-ops.
    const scheduleRefetch = () => {
      if (cancelled || pendingTimer) return;
      pendingTimer = setTimeout(() => {
        pendingTimer = null;
        fetchOnce();
      }, Math.random() * 8000);
    };

    fetchOnce();

    // SSE: refetch on any game update — cheap because the endpoint
    // short-circuits if there are no live games.
    const unsub = subscribeSSE("/sse/feed", {
      onMessage: (msg) => {
        if (msg?.type === "game:update" || msg?.type === "live:tick") scheduleRefetch();
      },
    });

    // Polling fallback — handles the case where SSE silently drops.
    // 30s base + per-client random offset so clients don't sync up.
    const id = setInterval(scheduleRefetch, 30_000 + Math.random() * 10_000);

    return () => {
      cancelled = true;
      if (pendingTimer) clearTimeout(pendingTimer);
      unsub();
      clearInterval(id);
    };
  }, []);

  return data;
}
